import React, {useMemo} from "react";
import {useFormContext, useWatch} from "react-hook-form";
import {FormattedMessage, useIntl} from "react-intl";
import {twJoin} from "tailwind-merge";
import {defaultBorderColor} from "peggirkit";
import {ArrowDownIcon, ArrowUpIcon, XMarkIcon} from "@heroicons/react/16/solid";
import {Menu} from "@/data/entities/menu";
import {OptionGroup} from "@/data/entities/option";
import {ProductEditValues} from "@/data/entities/menuEditor";
import {label} from "@/utils/LabelUtils";
import MultiSelectCombobox, {MultiSelectOption} from "@widgets/input/combobox/MultiSelectCombobox";
import {sortOptionGroups} from "@widgets/menuEditor/optionGroups/optionGroupsLookup";
import OptionGroupTypeLabel from "@widgets/menuEditor/optionGroups/OptionGroupTypeLabel";

type Props = {
    menu: Menu,
    disabled: boolean,
};

const iconButtonClassName = twJoin(
    "p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900",
    "dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-white",
    "disabled:opacity-30 disabled:pointer-events-none",
);

/**
 * Keeps the current order, drops deselected groups and adds new ones at the
 * end.
 */
const mergeSelection = (current: number[], selected: number[]): number[] => [
    ...current.filter(id => selected.includes(id)),
    ...selected.filter(id => !current.includes(id)),
];

const move = (ids: number[], index: number, offset: -1 | 1): number[] => {
    const result = [...ids];
    [result[index], result[index + offset]] = [result[index + offset], result[index]];
    return result;
};

/**
 * The option groups of the product, in the order they're shown.
 */
const OptionGroupsField = ({menu, disabled}: Props) => {
    const intl = useIntl();
    const {control, setValue} = useFormContext<ProductEditValues>();
    const optionGroupIds: number[] = useWatch({control, name: "optionGroupIds"});
    const groupsById: Map<number, OptionGroup> = useMemo(
        () => new Map(menu.optionGroups.map(group => [group.id, group])),
        [menu.optionGroups]);
    const options: MultiSelectOption<number>[] = useMemo(
        () => sortOptionGroups(menu.optionGroups).map(group => ({
            value: group.id,
            displayValue: group.name,
            description: intl.formatMessage({id: "nOptions"}, {count: group.optionIds.length}),
        })),
        [menu.optionGroups, intl]);
    const update = (ids: number[]) => setValue("optionGroupIds", ids, {shouldDirty: true});

    return (
        <div className={"w-full flex flex-col gap-3"}>
            <MultiSelectCombobox<number>
                label={label("optionGroups", false)}
                tip={intl.formatMessage({id: "optionGroupsOrder.tip"})}
                options={options}
                value={optionGroupIds}
                onChange={(selected: number[]) => update(mergeSelection(optionGroupIds, selected))}
                showSelected={false}
                disabled={disabled}
            />

            {
                optionGroupIds.length === 0
                    ? (
                        <p className={"text-sm text-gray-500 dark:text-slate-400"}>
                            <FormattedMessage id={"noOptionGroupsLinked"}/>
                        </p>
                    )
                    : (
                        <ol
                            className={twJoin(
                                "w-full rounded-lg border divide-y text-sm",
                                defaultBorderColor,
                                "divide-gray-200 dark:divide-slate-700",
                            )}
                        >
                            {optionGroupIds.map((groupId: number, index: number) => {
                                const group: OptionGroup | undefined = groupsById.get(groupId);

                                return (
                                    <li
                                        key={groupId}
                                        className={"flex flex-row items-center gap-3 py-2 pl-3 pr-2"}
                                    >
                                        <span className={"w-5 shrink-0 text-gray-400 tabular-nums"}>
                                            {index + 1}.
                                        </span>

                                        <div className={"min-w-0 flex flex-col"}>
                                            <span className={"font-medium text-gray-900 dark:text-white truncate"}>
                                                {group?.name ?? `#${groupId}`}
                                            </span>
                                            {
                                                group &&
                                                <span className={"text-xs text-gray-500 dark:text-slate-400"}>
                                                    <OptionGroupTypeLabel group={group}/>
                                                    {" · "}
                                                    <FormattedMessage
                                                        id={"nOptions"}
                                                        values={{count: group.optionIds.length}}
                                                    />
                                                </span>
                                            }
                                        </div>

                                        <div className={"ml-auto flex flex-row items-center gap-0.5 shrink-0"}>
                                            <button
                                                type={"button"}
                                                className={iconButtonClassName}
                                                disabled={disabled || index === 0}
                                                onClick={() => update(move(optionGroupIds, index, -1))}
                                                title={intl.formatMessage({id: "moveUp"})}
                                            >
                                                <ArrowUpIcon className={"size-4"}/>
                                                <span className={"sr-only"}><FormattedMessage id={"moveUp"}/></span>
                                            </button>
                                            <button
                                                type={"button"}
                                                className={iconButtonClassName}
                                                disabled={disabled || index === optionGroupIds.length - 1}
                                                onClick={() => update(move(optionGroupIds, index, 1))}
                                                title={intl.formatMessage({id: "moveDown"})}
                                            >
                                                <ArrowDownIcon className={"size-4"}/>
                                                <span className={"sr-only"}><FormattedMessage id={"moveDown"}/></span>
                                            </button>
                                            <button
                                                type={"button"}
                                                className={twJoin(iconButtonClassName, "hover:text-red-600 dark:hover:text-red-400")}
                                                disabled={disabled}
                                                onClick={() => update(optionGroupIds.filter(id => id !== groupId))}
                                                title={intl.formatMessage({id: "unlink"})}
                                            >
                                                <XMarkIcon className={"size-4"}/>
                                                <span className={"sr-only"}><FormattedMessage id={"unlink"}/></span>
                                            </button>
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    )
            }
        </div>
    );
};

export default OptionGroupsField;